// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import MaintenancePage from '@pages/MaintenancePage';
import { useAuthStore } from '@stores/authStore';
import ErrorBoundary from '@components/ErrorBoundary';
import OfflineBanner from '@components/OfflineBanner';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { SkeletonChatLayout } from '@components/ui/Skeleton';
import { migrateExternalServicesToProviderModelConfigOnce } from '@utils/modelConfigMigration';
import { fetchMaintenanceStatus } from '@utils/runtimeStatusProbe';
import { resolvePostAuthPath } from '@utils/authRouting';
import { DEEP_AGENTS } from '@/config/deepAgentRegistry';
import { useI18n } from '@hooks/useI18n';
import LatticeCursorHalo from '@components/design-system/LatticeCursorHalo';
import { RESEARCH_STUDIO_SURFACE } from '@/research-workbench/productSurfaceAuthority';
import { RESEARCH_WORKBENCH_SURFACE } from '@/research-workbench/surfaceAuthority';

// §3.1 首屏分层：营销壳/内容与聊天运行时全部按登录态代码分割——
// 未登录首屏不下载 CopilotKit 与聊天运行时，已登录不下载营销区块。
const HomePage = lazy(() => import('@pages/HomePage'));
const HomeSections = lazy(() => import('@pages/homepage/HomeSections'));
const MainChatRuntimePage = lazy(() => import('@pages/MainChatRuntimePage'));
const ResponsiveLayout = lazy(() => import('@components/ResponsiveLayout'));

const MAINTENANCE_NETWORK_FALLBACK_ENABLED = import.meta.env.MODE !== 'test';
const SettingsPage = lazy(() => import('@pages/SettingsPage'));
const KnowledgeBasePage = lazy(() => import('@pages/KnowledgeBasePage'));
const WorkspacePage = lazy(() => import('@pages/WorkspacePage'));
const SessionRoute = lazy(() => import('@/features/navigation/SessionRoute'));
const ChangelogPage = lazy(() => import('@pages/ChangelogPage'));
const RoadmapPage = lazy(() => import('@pages/RoadmapPage'));
const AboutPage = lazy(() => import('@pages/AboutPage'));
const TeamPage = lazy(() => import('@pages/TeamPage'));
const LoginPage = lazy(() => import('@pages/LoginPage'));
const ResearchStudioPage = lazy(() => import('@pages/ResearchStudioPage'));
const RegisterPage = lazy(() => import('@pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('@pages/VerifyEmailPage'));
const SocialCallbackPage = lazy(() => import('@pages/SocialCallbackPage'));
const BindPhonePage = lazy(() => import('@pages/BindPhonePage'));
const ForgotPasswordPage = lazy(() => import('@pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@pages/ResetPasswordPage'));
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));
// Research Workbench is a frontend shell over the existing bounded runtimes,
// not a Deep Agent — so it has a dedicated route, not a `DEEP_AGENTS` entry
// (R1-1 direct route mount seam; entitlement gate lives inside the page).
const ResearchWorkbenchPage = lazy(() => import('@pages/ResearchWorkbenchPage'));
const LearningWorkspacePage = lazy(() => import('@pages/LearningWorkspacePage'));

// Admin pages
const AdminLayout = lazy(() =>
  import('@components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout }))
);
const AdminDashboardPage = lazy(() => import('@pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('@pages/admin/AdminUsersPage'));
const AdminUsagePage = lazy(() => import('@pages/admin/AdminUsagePage'));
const AdminSessionsPage = lazy(() => import('@pages/admin/AdminSessionsPage'));
const AdminSystemPage = lazy(() => import('@pages/admin/AdminSystemPage'));
const AdminSettingsPage = lazy(() => import('@pages/admin/AdminSettingsPage'));
const AdminSubscriptionsPage = lazy(() => import('@pages/admin/AdminSubscriptionsPage'));
const AdminOrdersPage = lazy(() => import('@pages/admin/AdminOrdersPage'));
const AdminPlansPage = lazy(() => import('@pages/admin/AdminPlansPage'));
const AdminBugReportsPage = lazy(() => import('@pages/admin/AdminBugReportsPage'));

export function resolveRootRoute(isAuthed: boolean, userRole?: string) {
  if (!isAuthed) {
    // Guest: 首页壳（demo 视觉导航 + Outlet + Footer），不再包 LandingLayout
    return <HomePage />;
  }
  // P3-3（BUG-5，方案二）：admin 不再在 `/` 硬跳转——登录后的默认落地
  // 已由 resolvePostAuthPath 路由到 /admin；admin 显式导航到 `/`（侧边栏
  // "进入聊天"入口）时放行主聊天，/kb 的"返回聊天"死链同时自然可达。
  void userRole;
  return <ResponsiveLayout />;
}

function App() {
  const { isAuthenticated, bootstrapAuth, authBootstrapDone, user } = useAuthStore();
  const { t } = useI18n();
  const authReady = authBootstrapDone;
  const isAuthed = authBootstrapDone && isAuthenticated;
  const postAuthPath = resolvePostAuthPath(user?.role);

  // ── Maintenance mode ─────────────────────────────────────────
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | undefined>();

  const checkMaintenance = useCallback(async () => {
    // CD-14: route through the operational-status probe authority instead of
    // inlining the /api/maintenance literal here.
    const result = await fetchMaintenanceStatus();
    if (result.ok) {
      if (result.data.active) {
        setMaintenanceActive(true);
        setMaintenanceMessage(result.data.message || undefined);
        return;
      }
      setMaintenanceActive(false);
      setMaintenanceMessage(undefined);
      return;
    }
    if (!MAINTENANCE_NETWORK_FALLBACK_ENABLED || result.kind !== 'network') {
      setMaintenanceActive(false);
      setMaintenanceMessage(undefined);
      return;
    }
    // Network error during deployment — assume still upgrading.
    // CD-19: the fallback message is sourced from the i18n authority instead
    // of an inline Chinese literal.
    setMaintenanceActive(true);
    setMaintenanceMessage(t('app.maintenance.networkFallbackMessage'));
  }, [t]);

  useEffect(() => {
    void checkMaintenance();
  }, [checkMaintenance]);

  // Poll every 15s when in maintenance mode so the page auto-recovers
  useEffect(() => {
    if (!maintenanceActive) return;
    const id = setInterval(() => {
      void checkMaintenance();
    }, 15_000);
    return () => clearInterval(id);
  }, [maintenanceActive, checkMaintenance]);

  /**
   * 防止 React.StrictMode 下 useEffect 双执行导致并发触发迁移（尤其是迁移是异步的）。
   * - 使用 ref 做”进程内去重”
   * - 幂等仍由 localStorage flag 兜底，避免多 Tab/刷新重复迁移
   */
  const migrationStartedRef = useRef(false);

  useEffect(() => {
    if (!authBootstrapDone) {
      void bootstrapAuth();
    }
  }, [authBootstrapDone, bootstrapAuth]);

  useEffect(() => {
    if (!authBootstrapDone) return;
    if (!isAuthed) return;
    // G7-L: 迁移写入走 PUT /api/model-config（服务端 admin 守卫），普通用户执行
    // 必然 403——旧配置搬迁是管理员侧一次性动作，非 admin 首登直接跳过。
    if (user?.role !== 'admin') return;
    if (migrationStartedRef.current) return;
    migrationStartedRef.current = true;
    void (async () => {
      await migrateExternalServicesToProviderModelConfigOnce();
    })();
  }, [authBootstrapDone, isAuthed, user?.role]);

  if (maintenanceActive) {
    // Allow authenticated admins through so they can disable maintenance mode.
    // Block all other users (including unauthenticated) on every route.
    const isAdminUser = authReady && isAuthed && user?.role === 'admin';
    if (!isAdminUser) {
      return (
        <ErrorBoundary>
          <MaintenancePage message={maintenanceMessage} />
        </ErrorBoundary>
      );
    }
  }
  // ── End maintenance mode ─────────────────────────────────────

  if (!authReady) {
    return (
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error('[ErrorBoundary]', error.message, errorInfo.componentStack);
        }}
      >
        <OfflineBanner />
        <SkeletonChatLayout className="h-screen" />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        /* Global error reporting hook — extend with external service (e.g. Sentry) as needed */
        console.error('[ErrorBoundary]', error.message, errorInfo.componentStack);
      }}
    >
      <a href="#main-content" className="skip-to-content">
        {t('app.a11y.skipToContent')}
      </a>
      <OfflineBanner />
      {/* 批次 8 M6：Lattice 光标光环（细指针设备 + 非减弱动态时生效） */}
      <LatticeCursorHalo />
      <Suspense fallback={<SkeletonChatLayout className="h-screen" />}>
        <Routes>
          <Route
            path="/login"
            element={isAuthed ? <Navigate to={postAuthPath} replace /> : <LoginPage />}
          />
          <Route
            path="/register"
            element={isAuthed ? <Navigate to={postAuthPath} replace /> : <RegisterPage />}
          />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/forgot-password"
            element={isAuthed ? <Navigate to={postAuthPath} replace /> : <ForgotPasswordPage />}
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/social-callback" element={<SocialCallbackPage />} />
          <Route
            path="/bind-phone"
            element={isAuthed ? <BindPhonePage /> : <Navigate to="/login" />}
          />
          <Route
            path="/settings"
            element={isAuthed ? <SettingsPage /> : <Navigate to="/login" />}
          />
          {/* I16：/mastery 使用面迁出 settings → /learning（目标/计划/证据投影） */}
          <Route
            path="/mastery"
            element={
              isAuthed ? <Navigate to="/learning" replace /> : <Navigate to="/login" />
            }
          />
          {/* Root — guest HomePage 壳(未登录) / ResponsiveLayout(已登录) */}
          <Route path="/" element={resolveRootRoute(isAuthed, user?.role)}>
            <Route index element={isAuthed ? <MainChatRuntimePage /> : <HomeSections />} />
          </Route>
          {/* 批次 8.1：营销子页永远走营销壳（HomePage = .v4-demo + DemoNav +
              Footer）。此前它们挂在 resolveRootRoute 布局下，登录态被包进
              ResponsiveLayout，丢掉 .v4-demo 包装后 homepage.css/v4-subpages.css
              全部失配——无卡片布局、波浪分隔 SVG 变黑块、整页灰洗。 */}
          <Route path="/" element={<HomePage />}>
            <Route path="changelog" element={<ChangelogPage />} />
            <Route path="roadmap" element={<RoadmapPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="team" element={<TeamPage />} />
          </Route>
          <Route path="/kb" element={isAuthed ? <KnowledgeBasePage /> : <Navigate to="/login" />} />
          {/* Compatibility routes emitted by older/sidebar links; session route
              resolves the owner-scoped session before returning to the main chat. */}
          <Route path="/chat" element={isAuthed ? <Navigate to="/" replace /> : <Navigate to="/login" />} />
          <Route path="/chat/:sessionId" element={isAuthed ? <SessionRoute /> : <Navigate to="/login" />} />
          <Route path="/workspaces/:workspaceId" element={isAuthed ? <WorkspacePage /> : <Navigate to="/login" />} />
          {/* I16：学习空间（目标/计划/证据投影；服务端数据唯一权威） */}
          <Route path="/learning" element={isAuthed ? <LearningWorkspacePage /> : <Navigate to="/login" />} />
          <Route path="/copilot-physics" element={<Navigate to="/" replace />} />
          {DEEP_AGENTS.map((agent) => {
            const AgentPage = agent.page;
            return (
              <Route
                key={agent.path}
                path={agent.path}
                element={isAuthed ? <AgentPage /> : <Navigate to="/login" />}
              />
            );
          })}
          {/* /research-workbench — single-page shell over modeling + paper
              workbenches. Not a Deep Agent (no bounded runtime identity), so it
              has a dedicated route parallel to the DEEP_AGENTS map. Entitlement
              gating is resolved inside the page via the PRE-001 resolver. */}
          <Route
            path={RESEARCH_WORKBENCH_SURFACE.path}
            element={isAuthed ? <ResearchWorkbenchPage /> : <Navigate to="/login" />}
          />
          <Route
            path={RESEARCH_STUDIO_SURFACE.path}
            element={<ResearchStudioPage />}
          />
          {/* Admin routes — guarded by role */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="usage" element={<AdminUsagePage />} />
            <Route path="sessions" element={<AdminSessionsPage />} />
            <Route path="bugs" element={<AdminBugReportsPage />} />
            <Route path="system" element={<AdminSystemPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="orders" element={<AdminOrdersPage />} />
            <Route path="plans" element={<AdminPlansPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
